<template>
  <div v-if="canRead">
    <b-card no-body class="mt-2 p-2">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <div class="d-flex align-items-center">
          <h5 class="mb-0">Pix Parcelado</h5>
          <b-button id="pix-installment-plan-info" variant="flat" class="btn-icon form-horizontal p-0 ml-50">
            <feather-icon icon="InfoIcon" size="16" class="text-muted cursor-pointer" />
          </b-button>
          <b-popover target="pix-installment-plan-info" triggers="hover" placement="top">
            Pagamento via link Pix Automático (Asaas), dividido em parcelas, como alternativa ao
            parcelamento no cartão — sem comprometer o limite de crédito do cliente. O cliente escolhe essa
            forma de pagamento na entrevista e a autorização é criada automaticamente no checkout; as
            parcelas seguintes são debitadas sem nenhuma ação do backoffice.
          </b-popover>
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-content-center py-2">
        <b-spinner variant="primary" small />
      </div>

      <div v-else-if="!plans.length" class="text-muted">
        Nenhum Pix Parcelado registrado para este contrato.
      </div>

      <template v-else>
        <b-form-group v-if="plans.length > 1" label="Plano" label-for="pix-plan-select" label-cols="auto">
          <v-select id="pix-plan-select" v-model="selectedPlanId" :options="plans" label="id"
            :reduce="plan => plan.id" :clearable="false">
            <template #option="plan">#{{ plan.id }} - {{ plan.status_label }}</template>
            <template #selected-option="plan">#{{ plan.id }} - {{ plan.status_label }}</template>
          </v-select>
        </b-form-group>

        <b-row v-if="selectedPlan">
          <b-col md="12" sm="12" class="mt-1">
            <small class="text-muted">Status</small>
            <h5>
              <b-badge :variant="statusVariant(selectedPlan.status)">{{ selectedPlan.status_label }}</b-badge>
            </h5>
          </b-col>
          <b-col md="12" sm="12" class="mt-1">
            <small class="text-muted">Link de pagamento</small>
            <h5 v-if="selectedPlan.link" class="d-flex align-items-center">
              <a :href="selectedPlan.link" target="_blank" rel="noopener noreferrer" class="text-truncate mr-1"
                style="max-width: 320px; display: inline-block;">{{ selectedPlan.link }}</a>
              <feather-icon icon="CopyIcon" class="cursor-pointer" size="16" @click="copyLink(selectedPlan.link)" />
            </h5>
            <h5 v-else>Link ainda não cadastrado</h5>
          </b-col>
          <b-col md="6" sm="12" class="mt-1">
            <small class="text-muted">Valor total</small>
            <h5>{{ selectedPlan.total_value | toCurrency }}</h5>
          </b-col>
          <b-col md="6" sm="12" class="mt-1">
            <small class="text-muted">Próximo vencimento</small>
            <h5>{{ selectedPlan.next_due_date ? onlyDate(selectedPlan.next_due_date) : '-' }}</h5>
          </b-col>
          <b-col md="12" sm="12" class="mt-1">
            <small class="text-muted">
              Parcelas: {{ selectedPlan.paid_installments_count }} de {{ selectedPlan.installment_count }} pagas
            </small>
            <b-progress
              :value="selectedPlan.paid_installments_count"
              :max="selectedPlan.installment_count"
              class="mt-50"
              variant="success"
            />
          </b-col>
          <b-col md="6" sm="12" class="mt-1">
            <small class="text-muted">Responsável</small>
            <h5>{{ selectedPlan.responsible_user ? selectedPlan.responsible_user.name : '-' }}</h5>
          </b-col>
          <b-col md="6" sm="12" class="mt-1">
            <small class="text-muted">Última atualização</small>
            <h5>{{ selectedPlan.updated_at ? onlyDate(selectedPlan.updated_at) : '-' }}</h5>
          </b-col>

          <b-col v-if="canManage" cols="12" class="mt-1">
            <small class="text-muted">Mudar status</small>
            <div class="d-flex align-items-center">
              <v-select v-model="nextStatus" :options="allowedNextStatuses" label="label"
                :reduce="option => option.value" class="flex-grow-1 mr-1"
                placeholder="Selecione o novo status" />
              <b-button variant="outline-primary" size="sm" :disabled="!nextStatus || changingStatus"
                @click="changeStatus">
                {{ changingStatus ? 'Salvando...' : 'Aplicar' }}
              </b-button>
            </div>
          </b-col>

          <b-col cols="12" class="mt-2">
            <b-table
              :items="selectedPlan.payments"
              :fields="installmentFields"
              small
              striped
              borderless
              responsive
              show-empty
              empty-text="Nenhuma parcela cadastrada"
            >
              <template #cell(expiration_date)="data">{{ onlyDate(data.item.expiration_date) }}</template>
              <template #cell(amount)="data">{{ data.item.amount | toCurrency }}</template>
              <template #cell(status)="data">
                <b-badge :variant="installmentStatusVariant(data.item.status)">{{ data.item.status_description }}</b-badge>
              </template>
            </b-table>
            <small class="text-muted">
              Para reconciliar uma parcela manualmente (ex.: pagamento fora do fluxo automático), use a aba
              "Pagamentos" do contrato.
            </small>
          </b-col>
        </b-row>
      </template>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BPopover, BSpinner, BBadge, BProgress, BTable, BFormGroup,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapActions } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { onlyDate } from '@/helpers/converters'
import { MANAGE_ACTION, READ_ACTION, PIX_INSTALLMENT_PLAN_RESOURCE } from '@/constants/resources'
import * as types from '@/modules/contract/store/types'

const STATUS_TRANSITIONS = {
  aguardando_primeiro_pagamento: ['rodando', 'cancelado', 'erro_integracao'],
  rodando: ['inadimplente', 'concluido', 'cancelado'],
  inadimplente: ['rodando', 'concluido', 'cancelado'],
  concluido: [],
  cancelado: [],
  erro_integracao: ['cancelado'],
}

const STATUS_LABELS = {
  aguardando_primeiro_pagamento: 'Aguardando primeiro pagamento',
  rodando: 'Rodando Pix Parcelado',
  inadimplente: 'Inadimplente',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
  erro_integracao: 'Erro de integração',
}

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BPopover,
    BSpinner,
    BBadge,
    BProgress,
    BTable,
    BFormGroup,
    vSelect,
  },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      loading: false,
      plans: [],
      selectedPlanId: undefined,
      nextStatus: undefined,
      changingStatus: false,
      installmentFields: [
        { key: 'installment', label: '#' },
        { key: 'expiration_date', label: 'Vencimento' },
        { key: 'amount', label: 'Valor' },
        { key: 'status', label: 'Status' },
      ],
    }
  },
  computed: {
    canRead() {
      return this.$can(READ_ACTION, PIX_INSTALLMENT_PLAN_RESOURCE) || this.canManage
    },
    canManage() {
      return this.$can(MANAGE_ACTION, PIX_INSTALLMENT_PLAN_RESOURCE)
    },
    activePlan() {
      return this.plans.find(plan => plan.status === 'rodando')
    },
    selectedPlan() {
      return this.plans.find(plan => plan.id === this.selectedPlanId)
    },
    allowedNextStatuses() {
      if (!this.selectedPlan) return []
      const transitions = STATUS_TRANSITIONS[this.selectedPlan.status] || []
      return transitions.map(status => ({ value: status, label: STATUS_LABELS[status] }))
    },
  },
  mounted() {
    if (this.canRead) {
      this.loadPlans()
    }
  },
  methods: {
    ...mapActions({
      getPixInstallmentPlans: types.GET_PIX_INSTALLMENT_PLANS,
      updatePixInstallmentPlanStatus: types.UPDATE_PIX_INSTALLMENT_PLAN_STATUS,
    }),
    onlyDate,
    loadPlans() {
      this.loading = true
      this.getPixInstallmentPlans({ id: this.$route.params.id })
        .then(response => {
          this.plans = response.data.data || response.data
          const active = this.activePlan
          this.selectedPlanId = active ? active.id : this.plans[0]?.id
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao carregar o Pix Parcelado. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    copyLink(link) {
      navigator.clipboard.writeText(link)
      this.toast({
        component: ToastificationContent,
        props: {
          title: 'Sucesso!',
          text: 'Link copiado para a área de transferência.',
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    },
    statusVariant(status) {
      return {
        aguardando_primeiro_pagamento: 'light-warning',
        rodando: 'light-success',
        inadimplente: 'light-danger',
        concluido: 'light-info',
        cancelado: 'light-secondary',
        erro_integracao: 'light-danger',
      }[status] || 'light-secondary'
    },
    installmentStatusVariant(status) {
      return {
        paid: 'light-success',
        waiting_funds: 'light-secondary',
        waiting_payment: 'light-secondary',
        pending: 'light-secondary',
        pending_payment: 'light-secondary',
        processing: 'light-warning',
        refused: 'light-danger',
        error: 'light-danger',
        chargeback: 'light-danger',
        refunded: 'light-danger',
        canceled: 'light-secondary',
        unpaid: 'light-danger',
      }[status] || 'light-secondary'
    },
    changeStatus() {
      if (!this.nextStatus || !this.selectedPlan) return
      this.changingStatus = true
      this.updatePixInstallmentPlanStatus({ id: this.selectedPlan.id, status: this.nextStatus })
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Sucesso',
              text: 'Status do Pix Parcelado atualizado.',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.nextStatus = undefined
          this.loadPlans()
        })
        .catch(error => {
          const message = error?.response?.data?.message
            || 'Ocorreu um erro ao atualizar o status. Entre em contato com o setor de TI.'
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!', text: message, icon: 'AlertTriangleIcon', variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.changingStatus = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';
</style>
